import { Coordinates, Currency } from '../core/api/general/general.types';
import { OrderState } from '../core/api/order/order.types';
import { PaymentAccountType } from '../core/api/payment/payment.types';
import {
  CuisineType,
  DeliveryType,
  PickupLocationType,
  TableServiceCategory,
} from '../core/api/restaurant/restaurant.types';
import { DeliveryLocation } from '../core/api/user/user.types';

export enum GatewayPaymentAccount {
  Ideal = 'Ideal',
  Sofort = 'Sofort',
  Giropay = 'Giropay',
  Eps = 'Eps',
  Alipay = 'Alipay',
  Multibanco = 'Multibanco',
  Bancontact = 'Bancontact',
  P24 = 'P24',
}

export enum EmailRequestMode {
  DoNotRequest = 0,
  Request = 1,
  Require = 2,
}

export const BlankDeliveryLocation = function (Geo: Coordinates) {
  return {
    AutoSelectThisLocation: false,
    Building: '',
    Coordinates: Geo,
    DeliveryInstructions: '',
    DeliveryLocationId: -1,
    IsCustomAddress: false,
    IsDisplayed: true,
    Street: '',
    Town: '',
  };
};

export interface Customer {
  ConsentRequest: ConsentRequestData;
  CustomerName: string | null;
  CustomerPhoneNumberLocalFormatString: string;
  DefaultTipPercentage: number;
  DisplayTipOption: boolean;
  EmailAddress: string | null;
  EmailRequestMode: EmailRequestMode;
  IsGuest: boolean;
  Language: string;
  PhoneNumber: string;
  RequireAgeConfirmationForAlcoholOrdersData: RequireAgeConfirmationForAlcoholOrdersData;
  SmsPromotionsEnabled: boolean;
  UserId: number;
}

export interface RequireAgeConfirmationForAlcoholOrdersData {
  ConfirmButtonText: string;
  DenyButtonText: string;
  MessageText: string;
  DisplayConfirmationPrompt: boolean;
}

export interface ConsentRequestData {
  RequestConsent: boolean;
  MessageText: string | null;
  ConfirmButtonText: string | null;
  DenyButtonText: string | null;
  SmallPrintText: string | null;
  SmallPrintUrl: string | null;
}

export interface PaymentAccount {
  UserId: number;
  PaymentAccountId: number;
  CanRemoveFromCustomerView: boolean;
  Bin?: string;
  IsDefaultPaymentMethod: boolean;
  PaymentAccountType:
    | 'CardPaymentAccount'
    | 'CashPaymentAccount'
    | 'BrainTreePayPalPaymentAccount'
    | 'StripePayButtonPaymentAccount'
    | 'PayGreenRestaurantVoucher'
    | GatewayPaymentAccount;
  CustomerId: string;
  TokenizationKey: string;
  PublicApiKey: string;
  Description: string;
  SupportsSubmitOrderTips: boolean;
}

export interface PreviousOrderItemOptionDm {
  MenuItemOptionId: number;
  IsMasterOptionSetItem: boolean;
  Name: string;
  Price: number;
  MenuItemOptionDisplayOrder: number;
  MenuItemOptionSetDisplayOrder: number;
}

export interface PreviousOrderItemVms {
  VirtualRestaurantId: number;
  MenuSectionName: string;
  MenuSectionId: number;
  Name: string;
  Description: string;
  Price: number;
  PriceIncludingOptionSetItems: number;
  OrderItemId: number;
  MenuItemId: number;
  IsAvailable: boolean;
  PreviousOrderItemOptionVms: PreviousOrderItemOptionDm[];
  MenuSectionDisplayOrder: number;
  MenuItemDisplayOrder: number;
}

export interface CustomerOrderView {
  OrderId: number;
  VirtualRestaurantId: number;
  PhysicalRestaurantId?: number;
  DeliveryType: DeliveryType;
  RestaurantName: string;
  TipAmount: number;
  DeliveryAmount: number;
  FoodAmount: number;
  CardFee: number;
  TotalAmount: number;
  TsEditTipEndTimeUtc?: string; // time object
  TsUpdate: string; // time object
  UserRating?: number;
  CuisineType: CuisineType;
  PaymentAccountDescription: string; // Visa **** 2371. or Apple Pay. or Cash
  PreviousOrderItemVms: PreviousOrderItemVms[];
  IsHiddenFromRecentOrdersList: boolean;
  Currency: Currency;
  TsOrderPlaced: string; // date object
  DeliveryLocationAddressString: string; // eg. 20 Waterloo Road
  DeliveryLocationId?: number;
  DeliveryInstructions: string;
  CoordinatesDm: Coordinates;
  OrderState: OrderState;
}

export interface ApplyVoucherProps {
  orderId: number;
  voucherCode: string;
  paymentAccountId?: number;
}

export interface SubmitOrderBase {
  chefNote: string;
  excludeServiceCharge?: boolean;
  orderId: number;
  refererUrl?: string;
}
export interface SubmitOrderProps extends SubmitOrderBase {
  paymentAccountId: number;
}
export interface SubmitPayPalOrderProps extends SubmitOrderBase {
  clientToken: string;
  deviceData: string;
}
export interface SubmitPayStripeOrderProps extends SubmitOrderBase {
  stripePaymentMethodId: string;
  physicalRestaurantId?: number;
}

export interface PreviousOrderItemDm {
  MenuItemPublicId: string;
  MenuSectionPublicId: string;

  VirtualRestaurantId: number;
  MenuSectionName: string;
  MenuSectionId: number;
  Name: string;
  Description: string;
  Price: number;
  PriceIncludingOptionSetItems: number;
  OrderItemId: number;
  MenuItemId: number;
  IsAvailable: boolean;
  PreviousOrderItemOptionVms: PreviousOrderItemOptionDm[];
  MenuSectionDisplayOrder: number;
  MenuItemDisplayOrder: number;

  IsVoucher: boolean;

  Metadata: { [key: string]: string };
}

export enum RejectionReason {
  TooBusy = 0,
  FoodUnavailable = 1,
  UnableToDeliver = 2,
  UnknownAddress = 3,
  UnknownReason = 4,
  TooSoon = 5,
  TimeUnavailable = 6,
  DontDeliverToArea = 7,
  RestaurantUncontactable = 8,
}

export enum Warning {
  InsideFraudZone = 1,
  UnusuallyHighValueOrder = 2,
}
export enum DeliveryTrackingStatus {
  /** Not assigned to driver */
  Unassigned = 10,
  /** Assigned but not accepted */
  Unaccepted = 15,
  /** Accepted by driver */
  Accepted = 20,
  /** Carrying */
  Carrying = 30,
  /** On the way */
  OnTheWay = 60,
  /** Arrived at location */
  ArrivedAtLocation = 70,
  /** Delivered */
  Delivered = 80,
  /** Cannot deliver */
  CannotDeliver = 90,
}

export interface PreviousOrderDm {
  OrderId: number;
  LocalOrderId: string;
  VirtualRestaurantId: number;
  WhiteLabelId: number;
  UserId: number;
  PhysicalRestaurantId: number | null;
  DeliveryType: DeliveryType;
  PickupLocationType: PickupLocationType;
  PickupLocationId: number | null;
  PickupLocationOptionValue: string;
  TableServiceCatagory: TableServiceCategory;
  RestaurantName: string;
  TipAmount: number;
  DeliveryAmount: number;
  FoodAmount: number;
  CardFee: number;
  ProcessingFee: number;
  TotalAmount: number; // TotalCustomerAmount
  TsEditTipEndTimeUtc: Date | string | null;
  TsUpdate: Date | string;

  // response for 3d secure failure
  PaymentProcessingResponse: string | null;

  UserRating: number | null;

  CuisineType: CuisineType;
  /** Visa **** 2371. or Apple Pay. or Cash */
  PaymentAccountDescription: string;

  IsExternalPaymentAccountType: boolean;

  PreviousOrderItemVms: PreviousOrderItemDm[];

  IsHiddenFromRecentOrdersList: boolean;

  Currency: Currency;
  IsoCurrency: string;

  TsOrderPlaced: Date | string | null;

  DeliveryLocationAddressString: string; // eg. 20 Waterloo Road

  DeliveryLocationId: number | null;

  DeliveryLocation: DeliveryLocation;

  DeliveryInstructions: string;
  PhysicalRestaurantCoordinates: Coordinates;
  Coordinates: Coordinates;
  UserLocation: Coordinates;

  MenuId: number;

  RejectionReason: RejectionReason | null;
  KioskCustomerName: string;

  // #region RestaurantOrders

  TotalRestaurantAmount: number;

  PaymentAccountType: PaymentAccountType;

  OrderState: OrderState;

  CustomerName: string;

  EmailAddress: string;

  IsPreOrder: boolean;

  TsRequestedFor: Date | string | null;

  AmendmentForOrderId: number | null;

  HasAmendmentOrder: boolean;

  PreviousOrdersAtPhysicalRestaurantCount: number;
  PhysicalRestaurantTimeZoneInfoId: string;
  ChefNote: string;
  NetTipAmount: number;
  // Voucher: Voucher;
  VoucherAmount: number | null; // Eg. €5 off voucher will have an amount of -5.00
  // AppType: AppType;
  // PrintoutLayoutType: PrintoutLayoutType;
  UserMetadata: { [key: string]: string };
  PhysicalRestaurantMetadata: { [key: string]: string };

  Warnings: Warning;

  FoodTaxAmount: number;
  TotalTax: number;
  TipTaxAmount: number;
  DeliveryTaxAmount: number;
  // #endregion

  RefundedAmount: number | null;
  WasRedirectRequired: boolean;

  /** Delivery tracking status */
  DeliveryTrackingStatus: DeliveryTrackingStatus | null;

  /** Assigned driver identifier */
  DriverId: number | null;

  /** Time that the order was accepted for by store */
  AcceptedFor: Date | string | null;

  /** Id of user who rejected order */
  RejectedByUserId: number | null;

  RedirectUrl: string;

  /** Receipt code for external receipt* */
  ReceiptCode: string | null;
}

// ! Deprecated - use Result from src/core/types/data.types.ts
// Common reposonse JSON
export interface Result<T> {
  Code: number;
  Data: T;
  DeveloperMessage: string;
  StackTrace: string;
  Success: boolean;
  UserMessage: string;
}
